import { ComponentType } from '@angular/cdk/portal';
import { Component, computed, inject, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogContent, MatDialogClose } from '@angular/material/dialog';
import { PayerConfigItem, PayerRiskAdjustmentPolicy, PayerRiskAdjustmentPolicyUpdate } from '../../models/payer-config-item';
import { PayerAliasFilterSelectComponent } from '../../components/field-components/payer-alias-filter-select/payer-alias-filter-select.component';
import { UpdatePayerRequest } from '../../models/update-payer-request';
import { PayerConfigService } from '../../services/payer/payer-config.service';
import { ColDef, GetRowIdFunc, GetRowIdParams, GridReadyEvent, ICellRendererParams, RowClassRules, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { AgNoRowsOverlayComponent } from '../../components/ag-grid-components/ag-no-rows-overlay/ag-no-rows-overlay.component';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { PayerConfigEditRendererComponent } from '../../components/ag-grid-components/payer-config-edit-renderer/payer-config-edit-renderer.component';
import { AgYesNoPillRendererComponent } from '../../components/ag-grid-components/ag-yes-no-pill-renderer/ag-yes-no-pill-renderer.component';
import { AgTwoLineCellRendererComponent, IAgTwoLineCellRendererParams } from '../../components/ag-grid-components/ag-two-line-cell-renderer/ag-two-line-cell-renderer.component';
import { AddUpdateRiskPolicyDialogComponent, AddUpdateRiskPolicyDialogModel } from '../add-update-risk-policy-dialog/add-update-risk-policy-dialog.component';
import { Subscription } from 'rxjs';
import { AgGridFunctions } from '../../helpers/ag-grid-functions';
import { AgLoadingOverlayComponent } from '../../components/ag-grid-components/ag-loading-overlay/ag-loading-overlay.component';
import { MatFormField, MatError, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PayerEditStore } from '../../store/payer-edit.store';
import _ from 'lodash';
import { FileTypes } from '../../models/constants/file-types';
import { DialogHeaderComponent } from '../../components/layout-components/dialog-header/dialog-header.component';
import { DialogFooterComponent } from '../../components/layout-components/dialog-footer/dialog-footer.component';
import { CardSectionComponent } from '../../components/layout-components/card-section/card-section.component';
import { requiredTrimmedValidator } from '../../helpers/custom-validators';
import { PayerPolicyFunctions } from '../../helpers/payer-policy-functions';

export class AddUpdatePayerDialogModel {
  readonly component: ComponentType<any> = AddUpdatePayerDialogComponent;
  panelClass?: string | string[] = 'form-dialog';
  autoFocus?: boolean = false;
  data?: any;
  disableClose? = true;
  width? = '1040px';
  maxWidth? = '95dvw';
  maxHeight? = '90dvh';

  constructor(data: any) {
    this.data = data;
  }
}

// Service year dates come from the API as display strings (M/d/yyyy); unparseable values sort first.
const toTime = (value?: string) => {
  const time = value ? new Date(value).getTime() : NaN;
  return isNaN(time) ? -Infinity : time;
};

const dateComparator = (a?: string, b?: string) => {
  const diff = toTime(a) - toTime(b);
  return isNaN(diff) ? 0 : diff;
};

@Component({
    selector: 'app-add-update-payer-dialog',
    templateUrl: './add-update-payer-dialog.component.html',
    styleUrls: ['./add-update-payer-dialog.component.scss'],
    imports: [
        DialogHeaderComponent,
        DialogFooterComponent,
        CardSectionComponent,
        MatDialogContent,
        MatDialogClose,
        MatFormField,
        MatLabel,
        MatInput,
        MatError,
        ReactiveFormsModule,
        PayerAliasFilterSelectComponent,
        MatButton,
        MatIcon,
        MatTooltip,
        AgGridModule
    ]
})
export class AddUpdatePayerDialogComponent implements OnDestroy {

  actionType: string;

  payerForm: FormGroup;

  nameControlName = 'editPayerName';
  abbrevControlName = 'editPayerAbbreviation';

  // Column sizes for PayerName and Abbreviation; longer values were silently truncated on save.
  readonly nameMaxLength = 255;
  readonly abbreviationMaxLength = 32;

  nameControl = new FormControl('', [requiredTrimmedValidator(), Validators.maxLength(this.nameMaxLength)]);
  abbreviationControl = new FormControl('', [requiredTrimmedValidator(), Validators.maxLength(this.abbreviationMaxLength)]);

  isPayerAliasReady = false;

  // True once the payer or any of its service years has been saved; every close path returns it so
  // the payer list knows to refresh.
  hasPersistedChanges = false;

  request: UpdatePayerRequest = new UpdatePayerRequest();

  noRowsOverlayComponent: any = AgNoRowsOverlayComponent;
  noRowsOverlayComponentParams: any = {
    noRowsMessageFunc: () => this.addServiceYearEntryEnabled()
      ? 'No service years yet. Use Add Service Year to create one.'
      : 'Save the payer to add service years.'
  };

  loadingOverlayComponent: any = AgLoadingOverlayComponent;

  defaultColDef: ColDef = {
    editable: false,
    sortable: true,
    resizable: true,
    suppressMovable: true,
    suppressHeaderMenuButton: true,
    wrapHeaderText: true,
    autoHeaderHeight: true
  };

  columnDefs: ColDef[] = [
    {
      field: 'ServiceYearStart', headerName: 'Service Year',
      // Pinned columns ignore flex (AG Grid only flexes center columns), so size this one explicitly;
      // otherwise it defaults to 200px and pushes the grid into horizontal scroll.
      pinned: 'left', sort: 'desc', width: 160, minWidth: 160,
      comparator: dateComparator,
      cellRenderer: AgTwoLineCellRendererComponent,
      cellRendererParams: {
        primary: (params: ICellRendererParams) => this.serviceYearLabel(params.data),
        secondary: (params: ICellRendererParams) => this.serviceYearRange(params.data)
      } as IAgTwoLineCellRendererParams
    },
    {
      field: 'HasRiskAdjustment', headerName: 'Risk Adj. Policy',
      minWidth: 90, flex: 1,
      cellDataType: false,
      valueFormatter: AgGridFunctions.booleanValue,
      cellRenderer: AgYesNoPillRendererComponent
    },
    {
      field: 'HasManualOmit', headerName: 'Manually Omit',
      minWidth: 90, flex: 1,
      cellDataType: false,
      valueFormatter: this.manualOmitValue,
      cellRenderer: AgYesNoPillRendererComponent
    },
    {
      field: 'PreferredActionSubmissionType', headerName: 'Preferred Action Submission Type',
      minWidth: 120, flex: 1.2,
      cellClass: 'payer-dialog__code-cell',
      valueFormatter: this.filterOptionValue,
      comparator: (a, b) => (a?.Description ?? '').localeCompare(b?.Description ?? '')
    },
    {
      colId: 'ReceivingMOR', headerName: 'Receiving MOR',
      minWidth: 95, flex: 1,
      cellDataType: false,
      valueGetter: params => this.receivingMORValue(params),
      cellRenderer: AgYesNoPillRendererComponent
    },
    {
      colId: 'ReceivingMAO', headerName: 'Receiving MAO-004',
      minWidth: 100, flex: 1,
      cellDataType: false,
      valueGetter: params => this.receivingMAOValue(params),
      cellRenderer: AgYesNoPillRendererComponent
    },
    {
      colId: 'TemplateCount', headerName: 'Templates',
      type: 'rightAligned', minWidth: 95, flex: 0.9,
      valueGetter: params => this.templateCount(params.data),
      tooltip: (params) => this.templateNames(params.data)
    },
    {
      field: 'LastModifiedDate', headerName: 'Modified Date',
      minWidth: 105, flex: 1,
      cellClass: 'payer-dialog__muted-cell',
      comparator: dateComparator
    },
    {
      colId: 'edit', headerName: 'Edit',
      pinned: 'right', width: 52, minWidth: 52, maxWidth: 52,
      cellClass: 'payer-dialog__center-cell', headerClass: 'payer-dialog__center-header',
      sortable: false, resizable: false,
      cellRenderer: PayerConfigEditRendererComponent,
      cellRendererParams: { tooltip: 'Edit Service Year' }
    }
  ];

  public rowClassRules: RowClassRules = {
    // row style function
    'danger-cell': (params) => {
      let validDate = true;
      params.api?.forEachNode(n => {
        if(n.data.PolicyID == params.data.PolicyID) {
          return;
        }

        if(PayerPolicyFunctions.rangesOverlap(
          params.data.ServiceYearStart, params.data.ServiceYearEnd,
          n.data.ServiceYearStart, n.data.ServiceYearEnd)) {
          validDate = false;
          return;
        }
      });

      const emptyFields = (!params.data.ServiceYearStart || !params.data.ServiceYearEnd) ||
                          (params.data.HasRiskAdjustment && !params.data.HasManualOmit && !params.data.PreferredActionSubmissionType);
      params.context.componentParent.emptyPolicyFields = emptyFields;
      params.context.componentParent.invalidServiceYear = !validDate;

      const result = !validDate || emptyFields
      params.context.componentParent.invalidPolicyRows = result;

      return result;
    }
  };

  gridContext: any;

  invalidPolicyRows = false;
  invalidServiceYear = false;
  emptyPolicyFields = false;

  subs: Subscription[] = [];

  readonly data = inject(MAT_DIALOG_DATA);
  readonly payerEditStore = inject(PayerEditStore);
  private readonly snackBar = inject(MatSnackBar);

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;
  @ViewChild(PayerAliasFilterSelectComponent) aliasSelect: PayerAliasFilterSelectComponent;

  readonly addServiceYearEntryEnabled = computed(() => {
    const payer = this.payerEditStore.currentPayerItem();
    return payer?.PayerID != null && payer.PayerID > 0;
  });

  // "ABBR · Full Name" of the saved payer; empty until an added payer has been saved.
  readonly subheading = computed(() => {
    const payer = this.payerEditStore.currentPayerItem();
    return payer?.PayerID > 0 ? `${payer.Abbreviation} · ${payer.PayerName}` : '';
  });

  readonly serviceYearBadge = computed(() => {
    const count = this.payerEditStore.riskAdjustmentPolicies().length;
    return `${count} Service ${count === 1 ? 'Year' : 'Years'}`;
  });

  constructor(
    private configService: PayerConfigService,
    private fb: FormBuilder,
    private dialog: MatDialog
  ) {
    this.gridContext = { componentParent: this };
    this.actionType = this.data.actionType;

    this.payerForm = this.fb.group({});
    this.payerForm.addControl(this.nameControlName, this.nameControl);
    this.payerForm.addControl(this.abbrevControlName, this.abbreviationControl);

    this.payerEditStore.setCurrentPayerItem(this.data.payerItem || new PayerConfigItem());
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    // The store is app-wide; reset it however the dialog was closed.
    this.payerEditStore.clearState();
  }

  get canSave(): boolean {
    return this.payerForm.dirty && this.payerForm.valid && !this.invalidPolicyRows;
  }

  onSaveClick() {
    const payerItem = _.cloneDeep(this.payerEditStore.currentPayerItem());
    payerItem.PayerName = this.nameControl.value?.trim() ?? "";
    payerItem.Abbreviation = this.abbreviationControl.value?.trim() ?? "";

    this.request.Item = payerItem;

    this.request.AliasIDs = this.aliasSelect.getAliasIDsForPayer().toString();
    this.request.AliasNames = this.aliasSelect.getAliasNamesForPayer().toString();

    this.configService.addUpdatePayer(this.request).subscribe((result) => {
      const wasAdd = this.actionType == 'add';
      this.payerEditStore.setCurrentPayerItem(result as PayerConfigItem);
      this.payerForm.markAsPristine();
      this.hasPersistedChanges = true;
      // Once a new payer is saved, the dialog continues as Edit Payer.
      this.actionType = 'edit';
      this.snackBar.open(wasAdd ? 'Payer has been added!' : 'Payer has been updated!', null, { duration: 6000 });
    });
  }

  filterReadyCheck() {
    if(!this.isPayerAliasReady) {
      return;
    }

    if(this.actionType == 'edit') {
      this.setFilters();
    }
  }

  setFilters() {
    this.nameControl.setValue(this.payerEditStore.currentPayerItem().PayerName);
    this.abbreviationControl.setValue(this.payerEditStore.currentPayerItem().Abbreviation);

    const aliases = this.payerEditStore.currentPayerItem().Aliases?.split(", ");
    this.aliasSelect.setSelectedValuesByName(aliases);
  }

  onGridReady(params: GridReadyEvent) {
    // The no-rows overlay is driven by [rowData]: AG Grid shows it while the list is empty and hides it when rows arrive.
    if(this.actionType == 'add') {
      return;
    }

    this.refreshGridData(this.payerEditStore.currentPayerItem().PayerID);
  }

  getRowId: GetRowIdFunc = (params: GetRowIdParams) => {
    return `PolicyID:${params.data.PolicyID}, StartDate:${params.data.ServiceYearStart}, EndDate:${params.data.ServiceYearEnd}`;
  };

  serviceYearLabel(item?: PayerRiskAdjustmentPolicy): string {
    const start = item?.ServiceYearStart ? new Date(item.ServiceYearStart) : null;
    return start && !isNaN(start.getTime()) ? `CY ${start.getFullYear()}` : '';
  }

  serviceYearRange(item?: PayerRiskAdjustmentPolicy): string {
    if(!item?.ServiceYearStart && !item?.ServiceYearEnd) {
      return '';
    }

    return `${item.ServiceYearStart ?? ''} – ${item.ServiceYearEnd ?? ''}`;
  }

  manualOmitValue(item: ValueFormatterParams) {
    if(item.data.HasRiskAdjustment == false) {
      return "----";
    }

    return item.value ? "Yes" : "No";
  }

  receivingMORValue(params: ValueGetterParams) {
    return PayerPolicyFunctions.receivesFileType(params.data, FileTypes.MOR) ? 'Yes' : 'No';
  }

  receivingMAOValue(params: ValueGetterParams) {
    return PayerPolicyFunctions.receivesFileType(params.data, FileTypes.MAO) ? 'Yes' : 'No';
  }

  filterOptionValue(item: ValueFormatterParams) {
    if(!item.value) {
      return "----";
    }

    if(item.data.HasRiskAdjustment == false || item.data.HasManualOmit == true) {
      return "----";
    }

    return item.value.Description;
  }

  templateCount(item?: PayerRiskAdjustmentPolicy): number {
    return PayerPolicyFunctions.templateCount(item);
  }

  templateNames(item?: PayerRiskAdjustmentPolicy): string {
    return PayerPolicyFunctions.templateNames(item);
  }

  onAddPolicyClick() {
    const data = {mode: 'add', item: null}
    const dialogRef = this.dialog.open(AddUpdateRiskPolicyDialogComponent, new AddUpdateRiskPolicyDialogModel(data));

    this.subs.push(dialogRef.afterClosed().subscribe((result: PayerRiskAdjustmentPolicyUpdate) => {
      if(result) {
        this.upsertPolicy(result);
      }
    }));
  }

  // Called by the edit icon (PayerConfigEditRendererComponent) and on row double-click.
  onEditPayerClick(item: PayerRiskAdjustmentPolicy) {
    const data = {mode: 'edit', item: item}
    const dialogRef = this.dialog.open(AddUpdateRiskPolicyDialogComponent, new AddUpdateRiskPolicyDialogModel(data));

    this.subs.push(dialogRef.afterClosed().subscribe((result: PayerRiskAdjustmentPolicyUpdate) => {
      if(result) {
        this.upsertPolicy(result);
      }
    }));
  }

  // The Service Year dialog has already saved the policy (and the server assigns PolicyID), so reload
  // the payer and its policies rather than patching the row locally.
  upsertPolicy(result: PayerRiskAdjustmentPolicyUpdate) {
    this.hasPersistedChanges = true;
    this.refreshGridData(result.PayerID);
  }

  refreshGridData(id: number) {
    this.payerEditStore.setCurrentPayerItemById(id).subscribe(() => {
      this.payerEditStore.setRiskAdjustmentPolicies();
    })
  }

}
